import { Helmet } from "react-helmet-async";

import { useEffect, useRef, useState } from "react";
// @mui
import {
  Alert,
  Card,
  Stack,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import Iconify from "../components/iconify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { get, post, put } from "src/utils/networkUtils";
import { uploadImageAndGetUrl } from "src/utils/uploadImage";
import RichTextEditorDialog from "src/components/prize-draw-admin/RichTextEditor";
import HelpSlidesEditor, { DEFAULT_HELP_SLIDES, normalizeHelpSlides } from "src/components/hammer-smash/HelpSlidesEditor";

const EARN_MODE_COIN_BUNDLE = 0;
const EARN_MODE_AMOUNT = 1;
const COIN_ITEM_TYPE = 0;

const DATE_FIELDS = [
  { name: "promoStartDate", label: "Promo Start" },
  { name: "promoEndDate", label: "Promo End" },
  { name: "drawDate", label: "Draw Date (internal)" },
  { name: "smashStartDate", label: "Smash Start" },
  { name: "smashEndDate", label: "Smash End" },
];
// ----------------------------------------------------------------------
const getDefault = () => ({
  title: "",
  status: 0,
  earnMode: EARN_MODE_COIN_BUNDLE,
  storeItemIds: [],
  spendThreshold: "",
  maxRedemptions: 1,
  hammersPerRedemption: 5,
  promoStartDate: moment().utc().toISOString(),
  promoEndDate: moment().add(1, "week").utc().toISOString(),
  drawDate: moment().add(1, "week").add(1, "day").utc().toISOString(),
  smashStartDate: moment().add(1, "week").add(2, "day").utc().toISOString(),
  smashEndDate: moment().add(2, "week").utc().toISOString(),
  prizeImage: "",
  prizeTitle: "",
  prizeDescription: "",
  rules: "",
  checkoutTerms: "",
  helpSlides: DEFAULT_HELP_SLIDES,
});

// Mirrors the backend rule: promoStart < promoEnd <= draw <= smashStart < smashEnd
const getDateError = (campaign) => {
  const [promoStart, promoEnd, draw, smashStart, smashEnd] = DATE_FIELDS.map((field) =>
    moment(campaign[field.name]).valueOf()
  );
  if (promoStart >= promoEnd) return "Promo Start must be before Promo End";
  if (promoEnd > draw) return "Draw Date must be on or after Promo End";
  if (draw > smashStart) return "Smash Start must be on or after Draw Date";
  if (smashStart >= smashEnd) return "Smash Start must be before Smash End";
  return null;
};

export default function HammerSmashPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [earnModes, setEarnModes] = useState([]);
  const [storeItems, setStoreItems] = useState([]);
  const [searchParams] = useSearchParams();
  const rulesRef = useRef(null);
  const checkoutTermsRef = useRef(null);
  const isEdit = searchParams.has("campaign_id");
  const isDrawn = !!campaign?.drawnAt;

  useEffect(() => {
    if (isEdit) {
      get(`/admin/hammer-smash/${searchParams.get("campaign_id")}`)
        .then(function (response) {
          setCampaign({
            ...response.campaign,
            spendThreshold: response.campaign.spendThreshold ?? "",
            prizeImage: response.campaign.prizeImage || "",
            prizeDescription: response.campaign.prizeDescription || "",
            rules: response.campaign.rules || "",
            checkoutTerms: response.campaign.checkoutTerms || "",
            helpSlides: normalizeHelpSlides(response.campaign.helpSlides),
          });
          setLoading(false);
        });
    } else {
      setCampaign(getDefault());
      setLoading(false);
    }
    get(`/admin/hammer-smash/settings`)
      .then(function (response) {
        setEarnModes(response.earnModes);
      });
    get(`/admin/marketplace/store-items?limit=500`)
      .then(function (response) {
        setStoreItems((response.items || []).filter((item) => item.itemType === COIN_ITEM_TYPE));
      })
      .catch(function (error) {
        console.error("Error fetching store items:", error);
        setStoreItems([]);
      });
  }, [isEdit, searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCampaign((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (newValue, fieldName) => {
    if (!newValue || !moment(newValue).isValid()) return;
    setCampaign((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).utc().toISOString(),
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImageAndGetUrl(file);
      setCampaign((prevState) => ({ ...prevState, prizeImage: url }));
    } catch (error) {
      console.error("Error uploading prize image:", error);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const getStoreItemLabel = (storeItemId) => {
    const item = storeItems.find((storeItem) => storeItem.marketplaceStoreItemId === storeItemId);
    return item
      ? `${item.store === 1 ? "Google" : "Apple"} - ${item.name || item.description} (${item.price || item.productId})`
      : `Store Item ${storeItemId}`;
  };

  const saveHandler = () => {
    setLoading(true);
    const body = {
      ...campaign,
      spendThreshold: campaign.earnMode === EARN_MODE_AMOUNT ? +campaign.spendThreshold : null,
      storeItemIds: campaign.earnMode === EARN_MODE_COIN_BUNDLE ? campaign.storeItemIds : [],
      maxRedemptions: +campaign.maxRedemptions,
      hammersPerRedemption: +campaign.hammersPerRedemption,
    };
    const request = isEdit
      ? put(`/admin/hammer-smash/${searchParams.get("campaign_id")}`, body)
      : post(`/admin/hammer-smash`, body);

    request
      .then(function () {
        navigate("/dashboard/hammer-smash");
      })
      .catch(function (error) {
        console.error("Error saving hammer smash campaign:", error);
        setLoading(false);
      });
  };

  const dateError = campaign ? getDateError(campaign) : null;
  const earnRulesInvalid = campaign && (
    (campaign.earnMode === EARN_MODE_COIN_BUNDLE && !campaign.storeItemIds?.length) ||
    (campaign.earnMode === EARN_MODE_AMOUNT && !(+campaign.spendThreshold > 0)) ||
    !(+campaign.maxRedemptions > 0) ||
    !(+campaign.hammersPerRedemption > 0)
  );
  const maxHammers = campaign ? (+campaign.maxRedemptions || 0) * (+campaign.hammersPerRedemption || 0) : 0;

  return (
    <>
      <Helmet>
        <title> Hammer Smash </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Hammer Smash - {isEdit ? "Edit" : "Create New"}
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={() => {
                navigate("/dashboard/hammer-smash");
              }}
              startIcon={<Iconify icon="eva:close-fill" />}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => saveHandler()}
              startIcon={<Iconify icon="eva:save-fill" />}
              sx={{ mr: 1 }}
              disabled={
                loading ||
                uploading ||
                !campaign ||
                campaign.title.length < 3 ||
                campaign.prizeTitle.length < 1 ||
                !!dateError ||
                earnRulesInvalid
              }
            >
              Save
            </Button>
          </Box>
        </Stack>

        <Card sx={{ minHeight: 600 }}>
          {campaign === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ p: 3 }}>
              {isDrawn && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    The draw ran on {moment(campaign.drawnAt).format("YYYY-MM-DD HH:mm")}.
                    Earning rules and dates can no longer be changed.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={8}>
                <TextField
                  name="title"
                  label="Campaign Title"
                  fullWidth
                  value={campaign.title}
                  variant="outlined"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    id="status-select"
                    name="status"
                    value={campaign.status}
                    label="Status"
                    onChange={handleChange}
                  >
                    <MenuItem value={0}>Draft</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={2}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">How users earn hammers</Typography>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth disabled={isDrawn}>
                  <InputLabel id="earn-mode-select-label">Select By</InputLabel>
                  <Select
                    labelId="earn-mode-select-label"
                    id="earn-mode-select"
                    name="earnMode"
                    value={campaign.earnMode}
                    label="Select By"
                    onChange={handleChange}
                  >
                    {earnModes.map((earnMode) => (
                      <MenuItem key={earnMode.key} value={earnMode.key}>
                        {earnMode.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {campaign.earnMode === EARN_MODE_COIN_BUNDLE ? (
                <Grid item xs={8}>
                  <FormControl fullWidth disabled={isDrawn}>
                    <InputLabel id="store-items-select-label">Coin Bundles</InputLabel>
                    <Select
                      labelId="store-items-select-label"
                      id="store-items-select"
                      name="storeItemIds"
                      value={campaign.storeItemIds}
                      label="Coin Bundles"
                      onChange={handleChange}
                      multiple
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={getStoreItemLabel(value)} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {storeItems.map((item) => (
                        <MenuItem key={item.marketplaceStoreItemId} value={item.marketplaceStoreItemId}>
                          {getStoreItemLabel(item.marketplaceStoreItemId)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <Grid item xs={8}>
                  <TextField
                    name="spendThreshold"
                    label="Spend per redemption ($)"
                    type="number"
                    fullWidth
                    disabled={isDrawn}
                    value={campaign.spendThreshold}
                    onChange={handleChange}
                    helperText="Every full amount of total in-app spend during the promo counts as one redemption"
                  />
                </Grid>
              )}
              <Grid item xs={4}>
                <TextField
                  name="maxRedemptions"
                  label="Max Redemptions"
                  type="number"
                  fullWidth
                  disabled={isDrawn}
                  value={campaign.maxRedemptions}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  name="hammersPerRedemption"
                  label="Hammers per Redemption"
                  type="number"
                  fullWidth
                  disabled={isDrawn}
                  value={campaign.hammersPerRedemption}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {campaign.earnMode === EARN_MODE_AMOUNT && +campaign.spendThreshold > 0
                    ? `Up to ${maxHammers} hammers per user for $${+campaign.spendThreshold * (+campaign.maxRedemptions || 0)} total spend`
                    : `Up to ${maxHammers} hammers per user from purchases`}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Dates</Typography>
              </Grid>
              {DATE_FIELDS.map((field) => (
                <Grid item xs={4} key={field.name}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimePicker
                      label={field.label}
                      disabled={isDrawn}
                      value={moment(campaign[field.name])}
                      onChange={(value) => handleDateChange(value, field.name)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
              ))}
              {dateError && (
                <Grid item xs={12}>
                  <Alert severity="warning">{dateError}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1">Item to be won</Typography>
              </Grid>
              <Grid item xs={8}>
                <Stack spacing={2}>
                  <TextField
                    name="prizeTitle"
                    label="Prize Title"
                    fullWidth
                    value={campaign.prizeTitle}
                    onChange={handleChange}
                  />
                  <TextField
                    name="prizeDescription"
                    label="Prize Description"
                    fullWidth
                    multiline
                    minRows={4}
                    value={campaign.prizeDescription}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">Prize Image</Typography>
                  <IconButton color="primary" component="label" disabled={uploading}>
                    <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
                    <Iconify icon="eva:cloud-upload-fill" />
                  </IconButton>
                  {uploading && <CircularProgress size={20} />}
                  {campaign.prizeImage && !uploading && (
                    <IconButton
                      color="error"
                      onClick={() => setCampaign((prevState) => ({ ...prevState, prizeImage: "" }))}
                    >
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  )}
                </Stack>
                {campaign.prizeImage && (
                  <Box
                    component="img"
                    src={campaign.prizeImage}
                    alt="Prize"
                    sx={{ mt: 1, maxWidth: "100%", maxHeight: 200, borderRadius: 1 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Official Rules</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Full sweepstakes rules: eligibility, how to enter, mail-in entry addresses and deadlines.
                </Typography>
                <RichTextEditorDialog
                  editorRef={rulesRef}
                  value={campaign.rules}
                  onChange={(html) => setCampaign((prevState) => ({ ...prevState, rules: html }))}
                  fieldName="rules"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Checkout</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Short "No purchase necessary" text shown with purchases and on the Hammer Smash page.
                </Typography>
                <RichTextEditorDialog
                  editorRef={checkoutTermsRef}
                  value={campaign.checkoutTerms}
                  onChange={(html) => setCampaign((prevState) => ({ ...prevState, checkoutTerms: html }))}
                  fieldName="checkoutTerms"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Help Slides</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  The "How it works" popup users see on the Hammer Smash page.
                </Typography>
                <HelpSlidesEditor
                  slides={campaign.helpSlides}
                  onChange={(helpSlides) => setCampaign((prevState) => ({ ...prevState, helpSlides }))}
                />
              </Grid>
            </Grid>
          )}
        </Card>
      </Container>
    </>
  );
}
